import _ from 'lodash';
import { Component, OnInit, Input, inject, DestroyRef, ViewChild } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import {  UntypedFormGroup } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { selectCampaignSelected } from 'app/campaign/features/campaign.selectors';
import { select } from '@ngrx/store';
import { CampaignEventHandlerService } from 'app/campaign/services/campaign-event-handler.service';
import { DatetimeComponent } from 'app/core/components/datetime/datetime.component';

@Component({
  selector: 'app-campaign-event-settings',
  templateUrl: './campaign-event-settings.component.html',
  styleUrls: ['./campaign-event-settings.component.scss']
})
export class CampaignEventSettingsComponent implements OnInit {
  destroyRef = inject(DestroyRef)
  @ViewChild(DatetimeComponent) datetime: DatetimeComponent;
  @Input() formGroupCampaignTypeData: UntypedFormGroup = null;
  @Input() submitted: boolean = false;

  public initModel = new Date();
  public resetModel = new Date(0);
  public model = [];

  public timeZoneList = [];

  public test: any = null;

  public campaign$ = this.store.pipe(
    select(selectCampaignSelected)
  );
  
  constructor(
    private store: Store,
    public campaignEventHandlerService: CampaignEventHandlerService
  ) {
  }

  onStartTimeChange(event) {
    this.formGroupCampaignTypeData.get('startTime').setValue(event);
  }

  onEndTimeChange(event) {
    this.formGroupCampaignTypeData.get('endTime').setValue(event);
  }

  ngOnInit(): void {
    this.model = this.formGroupCampaignTypeData.get('eventDates').value;
    const zones = moment.tz.names();

    const startDateTimeGmtUnix = this.formGroupCampaignTypeData.get('startDateTimeGmtUnix');
    const timeZoneData = this.formGroupCampaignTypeData.get('timeZoneData');

    this.campaign$.pipe(
      filter(campaign => campaign !== null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const timestamp = this.formGroupCampaignTypeData.get('startDateTimeGmtUnix').value * 1000;

      this.timeZoneList = _.chain(zones).map((name, index) => {
        const offset = moment.tz.zone(name).utcOffset(timestamp);
        const gmt = `GMT ${moment.tz(timestamp, name).format('Z')}`;
        let abbr = moment.tz(timestamp, name).format('z');
        if(abbr.match(/[\+\-][0-9]+/) !== null) {
          abbr = gmt;
        }
        const label = `(${gmt}) ${name} - ${abbr}`;
        return { index, label, value: name, offset };
      }).sortBy('offset').value();
    });

    this.campaignEventHandlerService.onUpdateCampaign.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.datetime.updateValue(startDateTimeGmtUnix.value, timeZoneData.value.name);
    });

    timeZoneData.get('name').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const zone = moment.tz.zone(value);
      const abbrControl = timeZoneData.get('abbr');

      const timestamp = this.formGroupCampaignTypeData.get('startDateTimeGmtUnix').value * 1000;
      const abbr = zone.abbr(timestamp);

      abbrControl.setValue(abbr);
    });

    timeZoneData.get('abbr').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(abbr => {
      const abbrControl = timeZoneData.get('abbr');
      const nameControl = timeZoneData.get('name');
      const offsetControl = timeZoneData.get('offset');

      const zone = moment.tz.zone(nameControl.value);

      const index = _.findIndex(zone.abbrs, value => _.isEqual(value, abbr));
      const offset = zone.offsets[index] * -60;

      offsetControl.setValue(offset);

      const matches = abbr.match(/[\+\-][0-9]+/);
      const abbrValue = _.isArray(matches) ? `GMT ${matches[0]}`: abbr;
      
      abbrControl.setValue(abbrValue, { emitEvent: false });
    });

    this.formGroupCampaignTypeData.get('startDateTimeGmtUnix').valueChanges.pipe(
      filter(value => !_.isNull(value)),
      filter(() => {
        const nameControl = timeZoneData.get('name');
        return !_.isNull(nameControl.value)
      }),
      filter(() => {
        const abbrControl = timeZoneData.get('abbr');
        return !_.isNull(abbrControl.value)
      }),
      map(value => value * 1000),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const nameControl = timeZoneData.get('name');
      const abbrControl = timeZoneData.get('abbr');
      const zone = moment.tz.zone(nameControl.value);
      const abbr = zone.abbr(value);
      abbrControl.setValue(abbr);

      const timestamp = this.formGroupCampaignTypeData.get('startDateTimeGmtUnix').value * 1000;
      const zones = moment.tz.names();
      this.timeZoneList = _.chain(zones).map((name, index) => {
        const offset = moment.tz.zone(name).utcOffset(timestamp);
        const gmt = `GMT ${moment.tz(timestamp, name).format('Z')}`;
        let abbr = moment.tz(timestamp, name).format('z');
        if(abbr.match(/[\+\-][0-9]+/) !== null) {
          abbr = gmt;
        }
        const label = `(${gmt}) ${name} - ${abbr}`;
        return { index, label, value: name, offset };
      }).sortBy('offset').value();
    });

    this.formGroupCampaignTypeData.get('startDateTimeGmtUnix').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => { });
  }

  public dateClass = (date: Date) => {
    const index = this.model.map((m) => +m).indexOf(+date);
    if (index !== -1) {
      return [ 'selected' ];
    }
    return [ ];
  }

  public onDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      const date = event.value;
      const index = this.model.map((m) => +m).indexOf(+date);
      if (index === -1) {
        this.model.push(date);
      } else {
        this.model.splice(index, 1)
      }
      this.resetModel = new Date(0);
      this.formGroupCampaignTypeData.get('eventDates').setValue(this.model);
    }
  }

  public remove(date: Date): void {
    const index = this.model.map((m) => +m).indexOf(+date);
    this.model.splice(index, 1)
    this.formGroupCampaignTypeData.get('eventDates').setValue(this.model);
  }

}
